import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Skill {
  name: string;
  proficiency: number;
  description: string;
  yearsExperience: string;
}

interface SkillCategoryProps {
  title: string;
  icon: string;
  description: string;
  skills: Skill[];
  color: string;
}

const SkillCategory: React.FC<SkillCategoryProps> = ({
  title,
  icon,
  description,
  skills,
  color,
}) => {
  const getColorClasses = (colorName: string) => {
    const colors: Record<string, { bg: string; text: string; border: string; progress: string }> = {
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        border: 'border-blue-200',
        progress: 'bg-blue-500',
      },
      emerald: {
        bg: 'bg-emerald-50',
        text: 'text-emerald-600',
        border: 'border-emerald-200',
        progress: 'bg-emerald-500',
      },
      purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-600',
        border: 'border-purple-200',
        progress: 'bg-purple-500',
      },
      amber: {
        bg: 'bg-amber-50',
        text: 'text-amber-600',
        border: 'border-amber-200',
        progress: 'bg-amber-500',
      },
    };
    return colors[colorName] || colors.blue;
  };

  const colorClasses = getColorClasses(color);

  return (
    <div className="bg-card rounded-2xl border border-border p-6 lg:p-8 hover:shadow-elevated transition-all duration-300">
      <div className="flex items-start space-x-4 mb-6">
        <div className={`${colorClasses.bg} ${colorClasses.text} p-3 rounded-xl`}>
          <Icon name={icon as any} size={28} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
            {title}
          </h3>
          <p className="text-text-secondary text-sm leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {skills.map((skill, index) => (
          <div key={index} className="group">
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-semibold text-text-primary group-hover:text-primary transition-colors duration-200">
                  {skill.name}
                </h4>
                <p className="text-xs text-text-secondary mt-1">
                  {skill.yearsExperience} experience
                </p>
              </div>
              <div className={`${colorClasses.text} font-bold text-sm`}>
                {skill.proficiency}%
              </div>
            </div>

            <div className="relative h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`absolute top-0 left-0 h-full ${colorClasses.progress} rounded-full transition-all duration-1000 ease-out`}
                style={{ width: `${skill.proficiency}%` }}
              />
            </div>

            <p className="text-sm text-text-secondary mt-2 leading-relaxed">
              {skill.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SkillCategory;